/* eslint-disable @typescript-eslint/no-explicit-any */
import { T } from "@/i18n";
import { cn, getDateValue, sortDates } from "@/lib/utils";
import { Trash } from "lucide-react";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./button";
import { DatePicker } from "./date-picker";
import { Input } from "./input";

type KVObject = Record<string, any>;

interface KvProps {
  id?: string;
  value: KVObject;
  onChange: (obj: KVObject) => void;
  className?: string;
  valueTranslation?: string;
  addTranslation?: string;
  sort?: boolean;
}

export function formatDate(from: Date | undefined, to: Date | undefined) {
  return `${from?.getTime() ?? ""} - ${to?.getTime() ?? ""}`;
}

export const DateKv: FC<KvProps> = ({
  onChange,
  value,
  className,
  addTranslation,
  valueTranslation,
  sort,
  id,
}) => {
  const { t } = useTranslation();

  const definedValues = useMemo(
    () =>
      Object.entries(value).filter(([k]) => {
        const { from, to } = getDateValue(k);
        return !!from && !!to;
      }),
    [value],
  );
  const undefinedValues = useMemo(
    () =>
      Object.entries(value).filter(([k]) => {
        const { from, to } = getDateValue(k);
        return !from || !to;
      }),
    [value],
  );

  const render = (k: string, val: any, i: number) => {
    const dates = getDateValue(k);
    return (
      <div key={i} className="grid w-full gap-4">
        <div className="flex w-full items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            className="text-destructive"
            onClick={() => {
              const newValue = { ...value };
              delete newValue[k];
              onChange(newValue);
            }}
          >
            <Trash />
          </Button>
          <DatePicker
            className="w-full"
            mode="range"
            date={{ from: dates?.from, to: dates?.to }}
            setDate={(range) => {
              if (!range) return;
              const key = formatDate(range.from, range.to);
              if (key in value) {
                return;
              }
              const newValue = { ...value };
              delete newValue[k];
              newValue[key] = val;
              onChange(newValue);
            }}
          />
        </div>
        <div className="pl-14">
          <Input
            placeholder={t(valueTranslation ?? "common.inputs.kv.value")}
            value={val}
            className="w-full"
            onChange={(e) => {
              const newValue = { ...value };
              newValue[k] = e.target.value;
              onChange(newValue);
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={cn("flex flex-col gap-4", className)} id={id}>
      {definedValues
        .sort(([a], [b]) => (sort ? sortDates(a, b) : 0))
        .map(([k, val], i) => render(k, val, i))}
      {undefinedValues.map(([k, val], i) => render(k, val, i))}
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          onChange({ ...value, "": "" });
        }}
      >
        <T tKey={addTranslation ?? "common.inputs.kv.add"} />
      </Button>
    </div>
  );
};
